import { apiService } from './api';
import apiClient from './api';
import { ApiResponse } from '../types';
import {
  StagingSessionDto,
  StagingMetadataDto,
  StagingCommitResultDto,
  PercentileReferenceDto,
  ElementRankingDto,
  FilePreviewResult,
  ActiveStagingSession,
  StagingOwnerPairDto,
  ReassignOwnerGroupRequest,
} from '../types/staging';

const BASE_URL = '/staging';

export const stagingService = {
  /** Upload uses apiClient directly for multipart/form-data support. */
  upload: async (file: File, fileType?: string, labFormat?: string): Promise<ApiResponse<StagingSessionDto>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (fileType) formData.append('fileType', fileType);
    if (labFormat) formData.append('labFormat', labFormat);
    const response = await apiClient.post<ApiResponse<StagingSessionDto>>(`${BASE_URL}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  createFromWorkflow: (workflowId: number): Promise<ApiResponse<StagingSessionDto>> =>
    apiService.post<StagingSessionDto>(`${BASE_URL}/from-workflow/${workflowId}`),

  getSessions: (): Promise<ApiResponse<StagingSessionDto[]>> =>
    apiService.get<StagingSessionDto[]>(`${BASE_URL}/sessions`),

  getSession: (sessionId: string): Promise<ApiResponse<StagingSessionDto>> =>
    apiService.get<StagingSessionDto>(`${BASE_URL}/sessions/${sessionId}`),

  /** The workflow's current resumable staging session, or null (data is null when none exists). */
  getActiveSessionForWorkflow: (workflowId: number): Promise<ApiResponse<ActiveStagingSession | null>> =>
    apiService.get<ActiveStagingSession | null>(`${BASE_URL}/sessions/by-workflow/${workflowId}`),

  updateMetadata: (sessionId: string, metadata: Partial<StagingMetadataDto>): Promise<ApiResponse<StagingSessionDto>> =>
    apiService.put<StagingSessionDto>(`${BASE_URL}/sessions/${sessionId}/metadata`, metadata),

  cancelSession: (sessionId: string): Promise<ApiResponse<void>> =>
    apiService.delete<void>(`${BASE_URL}/sessions/${sessionId}`),

  updateNutrients: (rowId: number, updates: Record<string, number | null>): Promise<ApiResponse<void>> =>
    apiService.put<void>(`${BASE_URL}/rows/${rowId}/nutrients`, updates),

  updateReferences: (rowId: number, refs: { idFarmPlot?: number; idCropType?: number; idCropSubType?: number; landRef?: string }): Promise<ApiResponse<void>> =>
    apiService.put<void>(`${BASE_URL}/rows/${rowId}/references`, refs),

  autoFillLandRef: (sessionId: string): Promise<ApiResponse<number>> =>
    apiService.post<number>(`${BASE_URL}/sessions/${sessionId}/auto-fill-land-ref`),

  approveRows: (sessionId: string, rowIds: number[]): Promise<ApiResponse<void>> =>
    apiService.post<void>(`${BASE_URL}/sessions/${sessionId}/approve`, { rowIds }),

  rejectRows: (sessionId: string, rowIds: number[], reason?: string): Promise<ApiResponse<void>> =>
    apiService.post<void>(`${BASE_URL}/sessions/${sessionId}/reject`, { rowIds, reason }),

  /** confirmedOwners: the farmer/farm pairs the user confirmed in the commit summary (DRI-99). */
  commit: (
    sessionId: string,
    approvedRowIds: number[],
    confirmedOwners: StagingOwnerPairDto[],
  ): Promise<ApiResponse<StagingCommitResultDto>> =>
    apiService.post<StagingCommitResultDto>(`${BASE_URL}/sessions/${sessionId}/commit`, {
      approvedRowIds,
      confirmedOwners,
    }),

  /** Move one owner group to the session pair (toIdFarm null) or Set another farm of its farmer (DRI-99). */
  reassignOwnerGroup: (sessionId: string, request: ReassignOwnerGroupRequest): Promise<ApiResponse<StagingSessionDto>> =>
    apiService.post<StagingSessionDto>(`${BASE_URL}/sessions/${sessionId}/owner-groups/reassign`, request),

  getPercentiles: (cropSubTypeId: number, sampleType: string): Promise<ApiResponse<PercentileReferenceDto>> =>
    apiService.get<PercentileReferenceDto>(`${BASE_URL}/percentiles`, { cropSubTypeId, sampleType }),

  getPercentilesBatch: (cropSubTypeIds: number[], sampleType: string): Promise<ApiResponse<PercentileReferenceDto[]>> =>
    apiService.get<PercentileReferenceDto[]>(`${BASE_URL}/percentiles/batch`, {
      cropSubTypeIds: cropSubTypeIds.join(','),
      sampleType,
    }),

  getElementRankings: (): Promise<ApiResponse<ElementRankingDto[]>> =>
    apiService.get<ElementRankingDto[]>(`${BASE_URL}/element-rankings`),

  /** Preview files for lab-triggered workflow creation (parse + resolve, no persistence). */
  previewFiles: async (
    files: { sap?: File; tissue?: File; soil?: File },
    signal?: AbortSignal,
  ): Promise<ApiResponse<FilePreviewResult>> => {
    const formData = new FormData();
    if (files.sap) formData.append('sapFile', files.sap);
    if (files.tissue) formData.append('tissueFile', files.tissue);
    if (files.soil) formData.append('soilFile', files.soil);
    const response = await apiClient.post<ApiResponse<FilePreviewResult>>(
      `${BASE_URL}/preview-files`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
      },
    );
    return response.data;
  },
};
